import { useEffect, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { PROVINCES, wantedFiltersFromQuery, wantedFiltersToQuery, type Province, type WantedFilters } from "../../shared/wanted";
import { useAuth } from "../auth/AuthProvider";
import { Pagination } from "../components/Pagination";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import { imageUrl } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi } from "../market/api";
import { WantedTile } from "./components";
import { useGame } from "../lib/GameProvider";

/** /se-busca — the cards people are looking for, one tile per card (how many, never who). */
export function WantedPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const { game } = useGame();
  const filters = wantedFiltersFromQuery(params);
  const key = wantedFiltersToQuery({ ...filters, game });
  const board = useAsync((signal) => marketApi.wantedBoard({ ...filters, game }, signal), [key]);
  const matches = useAsync((signal) => (user ? marketApi.wantedMatches(signal) : Promise.resolve(null)), [user?.id]);

  useEffect(() => {
    document.title = `${t("wanted.metaTitle")} | pokefans.fun`;
  }, [t]);

  const apply = (change: Partial<WantedFilters>) => setParams(wantedFiltersToQuery({ ...filters, page: 1, ...change }));
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const province = String(f.get("provincia") ?? "");
    apply({ q: String(f.get("q") ?? "").trim(), trade: f.get("canje") === "1", priced: f.get("precio") === "1", province: (province || null) as Province | null, sort: f.get("orden") === "most" ? "most" : "recent" });
  };
  const filtering = Boolean(filters.q || filters.trade || filters.priced || filters.province);
  const totalPages = board.data ? Math.max(1, Math.ceil(board.data.total / board.data.pageSize)) : 1;
  const postTo = user ? "/se-busca/publicar" : `/ingresar?next=${encodeURIComponent("/se-busca/publicar")}`;

  return (
    <div className="container section wanted-page">
      <header className="wanted-head">
        <div>
          <h1>{t("wanted.title")}</h1>
          <p className="muted">{t("wanted.intro")}</p>
        </div>
        <Link to={postTo} className="btn btn-primary">
          {user ? t("wanted.post") : t("wanted.signInToPost")}
        </Link>
      </header>

      {matches.data && matches.data.cards > 0 && !matches.data.items && (
        // Without demand alerts: the numbers, and which cards with VMAX.
        <section className="wanted-matches" aria-labelledby="wanted-matches-title">
          <h2 id="wanted-matches-title">{t("wanted.matchesLocked", { people: t("wanted.people", { count: matches.data.wanting }), count: matches.data.cards })}</h2>
          <p className="muted small">{t("wanted.matchesLockedHint")}</p>
          <Link to="/vender/planes" className="btn btn-outline btn-sm">
            {t("wanted.matchesSeeWhich")}
          </Link>
        </section>
      )}
      {matches.data?.items && matches.data.items.length > 0 && (
        <section className="wanted-matches" aria-labelledby="wanted-matches-title">
          <h2 id="wanted-matches-title">{t("wanted.matches", { count: matches.data.cards })}</h2>
          <p className="muted small">{t("wanted.matchesHint")}</p>
          <ul>
            {matches.data.items.slice(0, 12).map((m) => (
              <li key={m.productId}>
                <Link to={`/se-busca/${encodeURIComponent(m.slug)}`}>
                  <img src={imageUrl(m.imageKey)} alt="" width={32} height={45} loading="lazy" />
                  <span>
                    {m.name} <span className="muted small">· {t("wanted.wanting", { count: m.wanting })}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <form className="wanted-filters" onSubmit={submit} key={key} role="search">
        <div className="field">
          <label htmlFor="wf-q">{t("wanted.search")}</label>
          <input id="wf-q" name="q" type="search" defaultValue={filters.q} placeholder={t("wanted.searchHint")} />
        </div>
        <div className="field">
          <label htmlFor="wf-prov">{t("wanted.province")}</label>
          <select id="wf-prov" name="provincia" defaultValue={filters.province ?? ""}>
            <option value="">{t("wanted.anyProvince")}</option>
            {(Object.keys(PROVINCES) as Province[]).map((p) => (
              <option key={p} value={p}>
                {PROVINCES[p]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="wf-sort">{t("wanted.sort")}</label>
          <select id="wf-sort" name="orden" defaultValue={filters.sort}>
            <option value="recent">{t("wanted.sort.recent")}</option>
            <option value="most">{t("wanted.sort.most")}</option>
          </select>
        </div>
        <label className="check">
          <input type="checkbox" name="canje" value="1" defaultChecked={filters.trade} /> {t("wanted.trade")}
        </label>
        <label className="check">
          <input type="checkbox" name="precio" value="1" defaultChecked={filters.priced} /> {t("wanted.priced")}
        </label>
        <div className="btn-row">
          <button type="submit" className="btn btn-primary">
            {t("wanted.apply")}
          </button>
          {filtering && (
            <button type="button" className="btn btn-link" onClick={() => setParams("")}>
              {t("wanted.clear")}
            </button>
          )}
        </div>
      </form>

      {board.error ? (
        <ErrorMessage onRetry={board.reload} />
      ) : !board.data ? (
        <Loading />
      ) : board.data.items.length === 0 ? (
        <div className="status">
          <p>{filtering ? t("wanted.emptyFiltered") : t("wanted.empty")}</p>
          {!filtering && (
            <Link to={postTo} className="btn btn-primary">
              {t("wanted.post")}
            </Link>
          )}
        </div>
      ) : (
        <>
          <p className="muted small" role="status">
            {t("wanted.results", { count: board.data.total })} · {t("wanted.haveOne")}
          </p>
          <ul className="wanted-grid">
            {board.data.items.map((c) => (
              <WantedTile key={c.productId} card={c} />
            ))}
          </ul>
          {totalPages > 1 && <Pagination page={filters.page} totalPages={totalPages} onPage={(page) => apply({ page })} />}
        </>
      )}
    </div>
  );
}
